import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import NewWorkerModal from '../components/NewWorkerModal';
import UniversalVoiceWizard from '../components/UniversalVoiceWizard';
import { CreateOperatorModal } from '../pages/Operators';
import { getWorkerIcon } from '../utils/workerIcons';
import {
  MagnifyingGlass,
  Lightning,
  Eye,
  Bell,
  Robot,
  CheckCircle,
  Clock,
  ArrowRight,
  EnvelopeSimple,
  CalendarBlank,
  Phone,
  ChatText,
  ArrowClockwise,
  CircleNotch,
  Warning,
  WarningCircle,
  Sparkle,
  Plus,
  Pulse,
  ShieldCheck,
  Wrench,
  PlugsConnected,
  Gauge,
  GearSix,
  Play,
  UserCircle,
  TrendUp,
  PaperPlaneTilt,
  PhoneOutgoing,
  CalendarCheck,
  Package,
} from '@phosphor-icons/react';

/* ─────────────────────────────────────────────── */
/*  FEATURE FLAG                                    */
/* ─────────────────────────────────────────────── */
const DASHBOARD_V1_FLAG = 'new_dashboard_v1';
const isDashboardV1 = () => {
  try { return localStorage.getItem(DASHBOARD_V1_FLAG) !== 'false'; }
  catch { return true; }
};

/* ─────────────────────────────────────────────── */
/*  HELPERS                                         */
/* ─────────────────────────────────────────────── */
function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ═══════════════════════════════════════════════ */
/*  PART 1 — TODAY SUMMARY STRIP                   */
/* ═══════════════════════════════════════════════ */
const TodaySummary = ({ runs, workItems }) => {
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todayRuns = runs.filter(r => new Date(r.created_at) >= todayStart);

  const enquiryRuns = todayRuns.filter(r =>
    r.trigger_source === 'email' || r.trigger_source === 'manual' || r.trigger_source === 'webhook'
  );
  const respondedRuns = todayRuns.filter(r =>
    r.status === 'completed' && r.reply
  );
  const bookedRuns = todayRuns.filter(r => {
    const name = (r.automation_name || '').toLowerCase();
    const summary = (r.summary || '').toLowerCase();
    return name.includes('book') || summary.includes('book') || summary.includes('confirmed') || summary.includes('scheduled');
  });
  const followUpRuns = todayRuns.filter(r => {
    const name = (r.automation_name || '').toLowerCase();
    return name.includes('follow') || name.includes('chase') || name.includes('reactivate');
  });

  // Get recency text for each metric
  const getRecency = (metricRuns) => {
    if (metricRuns.length === 0) return null;
    const latest = metricRuns.reduce((a, b) => new Date(a.created_at) > new Date(b.created_at) ? a : b);
    return `Last: ${timeAgo(latest.created_at)}`;
  };

  const metrics = [
    { label: 'New enquiries', value: enquiryRuns.length, recency: getRecency(enquiryRuns), icon: EnvelopeSimple, color: '#6366F1' },
    { label: 'Responded automatically', value: respondedRuns.length, recency: getRecency(respondedRuns), icon: PaperPlaneTilt, color: '#00E5C0' },
    { label: 'Jobs booked', value: bookedRuns.length, recency: getRecency(bookedRuns), icon: CalendarCheck, color: '#3B82F6' },
    { label: 'Follow-ups sent', value: followUpRuns.length, recency: getRecency(followUpRuns), icon: ArrowClockwise, color: '#F59E0B' },
  ];

  const hasActivity = metrics.some(m => m.value > 0);

  if (!hasActivity) return null;

  return (
    <section data-testid="today-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {metrics.map((m, i) => {
          const Icon = m.icon;
          const active = m.value > 0;
          return (
            <div key={m.label} data-testid={`summary-${m.label.toLowerCase().replace(/\s/g, '-')}`}
              className="flex items-center gap-3 py-3 animate-section-enter"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: active ? `${m.color}12` : 'rgba(255,255,255,0.06)' }}>
                <Icon size={16} style={{ color: active ? m.color : '#B0B8C4' }} weight="duotone" />
              </div>
              <div>
                <p className="text-xl font-bold leading-none" style={{ color: active ? m.color : '#B0B8C4' }}>
                  {m.value}
                </p>
                <p className="text-[11px] text-[#C8D1DB] mt-0.5 leading-tight">{m.label}</p>
                {m.recency && (
                  <p className="text-[9px] mt-0.5 leading-tight" style={{ color: `${m.color}99` }}>{m.recency}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════ */
/*  PART 2 — HUMAN-READABLE ACTIVITY FEED          */
/* ═══════════════════════════════════════════════ */
const humanizeActivity = (run) => {
  const src = (run.trigger_source || '').toLowerCase();
  const name = run.automation_name || 'Worker';
  const customer = run.input?.customer_name || run.customer_name || '';
  const summary = run.summary || '';
  const status = run.status || '';
  const hasReply = !!run.reply;

  // Build human-readable headline
  if (src === 'email' && hasReply) {
    const responseTime = run.processing_time_ms
      ? `${Math.round(run.processing_time_ms / 1000)}s`
      : '';
    return {
      headline: `New enquiry${customer ? ` from ${customer}` : ''}`,
      detail: responseTime ? `Responded in ${responseTime}` : 'Responded automatically',
      icon: PaperPlaneTilt,
      color: '#00E5C0',
    };
  }

  if (src === 'email' && status === 'needs_review') {
    return {
      headline: `New enquiry${customer ? ` from ${customer}` : ''}`,
      detail: 'Waiting for your review',
      icon: EnvelopeSimple,
      color: '#F59E0B',
    };
  }

  const nameLower = name.toLowerCase();
  if (nameLower.includes('follow') || nameLower.includes('chase')) {
    return {
      headline: `Follow-up sent${customer ? ` to ${customer}` : ''}`,
      detail: summary || 'Quote pending',
      icon: ArrowClockwise,
      color: '#A78BFA',
    };
  }

  if (nameLower.includes('book') || summary.toLowerCase().includes('book')) {
    const amount = summary.match(/\$[\d,]+/);
    return {
      headline: `Booking confirmed${customer ? ` — ${customer}` : ''}`,
      detail: amount ? `${amount[0]} job` : summary || 'Appointment scheduled',
      icon: CalendarCheck,
      color: '#3B82F6',
    };
  }

  if (nameLower.includes('miss') && (src === 'phone' || nameLower.includes('call'))) {
    return {
      headline: `Missed call${customer ? ` from ${customer}` : ''}`,
      detail: 'SMS sent automatically',
      icon: PhoneOutgoing,
      color: '#EC4899',
    };
  }

  if (src === 'manual_test') {
    return {
      headline: `Test run completed`,
      detail: name,
      icon: Play,
      color: '#7D8896',
    };
  }

  // Default
  return {
    headline: summary || `Task handled${customer ? ` — ${customer}` : ''}`,
    detail: name,
    icon: Lightning,
    color: '#7D8896',
  };
};

const ActivityFeed = ({ runs, navigate }) => {
  const recent = runs.slice(0, 8);

  return (
    <section data-testid="activity-feed">
      <div className="flex items-center justify-between mb-5 mt-2">
        <h2 className="text-sm font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] flex-1">
          Recent activity
        </h2>
        {runs.length > 0 && (
          <button onClick={() => navigate('/inbox')}
            className="text-xs text-[#5C6778] hover:text-[#00E5C0] font-medium flex items-center gap-1 transition-colors">
            View all <ArrowRight size={12} />
          </button>
        )}
      </div>
      {recent.length === 0 ? (
        <div className="py-8 text-center">
          <Clock size={24} className="text-[#2A2F38] mx-auto mb-2" weight="light" />
          <p className="text-xs text-[#5C6778]">Activity will show here once your workers start handling tasks.</p>
        </div>
      ) : (
        <div data-testid="activity-list">
          {recent.map((run, idx) => {
            const a = humanizeActivity(run);
            const Icon = a.icon;
            return (
              <div key={run.id}>
                <div data-testid={`activity-${run.id}`}
                  className="flex items-center gap-4 py-3.5 px-1 group hover:bg-[rgba(255,255,255,0.015)] rounded-lg transition-colors cursor-pointer"
                  onClick={() => navigate('/inbox')}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${a.color}10` }}>
                    <Icon size={15} style={{ color: a.color }} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#F0F4F8] font-medium truncate">{a.headline}</p>
                    <p className="text-xs text-[#5C6778] mt-0.5 truncate">{a.detail}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: a.color }} />
                    <span className="text-[10px] text-[#4E5563]">{timeAgo(run.created_at)}</span>
                  </div>
                </div>
                {idx < recent.length - 1 && (
                  <div className="mx-1 border-b border-[rgba(255,255,255,0.03)]" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};


/* ═══════════════════════════════════════════════ */
/*  NEW DASHBOARD V1 — AI Team Overview            */
/* ═══════════════════════════════════════════════ */

/* ── Worker Card (larger, team-member style) ── */

/* Maps outcome_category → friendly display name */
const WORKER_DISPLAY = {
  enquiry: { name: 'Enquiry Responder', subtitle: 'Replies to new customer messages' },
  lead: { name: 'Lead Handler', subtitle: 'Captures and qualifies incoming leads' },
  booking: { name: 'Booking Assistant', subtitle: 'Schedules appointments for customers' },
  follow_up: { name: 'Follow-Up Assistant', subtitle: 'Re-engages leads who went quiet' },
  review: { name: 'Review Manager', subtitle: 'Sends review requests after jobs' },
  support: { name: 'Support Agent', subtitle: 'Answers common customer questions' },
  notification: { name: 'Notification Worker', subtitle: 'Keeps your team informed' },
};

const getWorkerDisplay = (worker) => {
  const cat = worker.outcome_category || worker.category || '';
  const match = WORKER_DISPLAY[cat];
  if (match) {
    return { name: match.name || worker.name, subtitle: match.subtitle };
  }
  // Derive subtitle from worker name or description
  const desc = worker.short_description || worker.description?.replace(/\*\*/g, '').substring(0, 60) || '';
  return {
    name: worker.name,
    subtitle: desc || '',
  };
};

const TeamWorkerCard = ({ worker, runs, navigate }) => {
  const isActive = worker.status === 'active';
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const workerRuns = (runs || []).filter(r => r.automation_id === worker.id);
  const todayRuns = workerRuns.filter(r => new Date(r.created_at) >= todayStart);
  const needsReview = todayRuns.filter(r => r.status === 'needs_review' || r.status === 'pending_review').length;
  const display = getWorkerDisplay(worker);

  const wType = (worker.automation_type || '').toLowerCase();
  const isProcessWorker = wType.includes('event') || wType.includes('enquiry') || wType.includes('trigger') || wType.includes('lead') || wType.includes('booking');
  const statusLabel = isActive
    ? (isProcessWorker ? 'Handling enquiries automatically' : 'Ready to use')
    : (isProcessWorker ? 'Not responding to new leads' : 'Paused');

  // Last action: human-readable most recent run
  const lastRun = workerRuns.length > 0
    ? workerRuns.reduce((a, b) => new Date(a.created_at) > new Date(b.created_at) ? a : b)
    : null;
  const getLastAction = () => {
    if (!lastRun) return null;
    const a = humanizeActivity(lastRun);
    const ago = timeAgo(lastRun.created_at);
    // Shorten headline for card
    const short = a.headline.length > 35 ? a.headline.substring(0, 35) + '...' : a.headline;
    return `${short} (${ago})`;
  };
  const lastAction = getLastAction();

  return (
    <div
      data-testid={`team-worker-${worker.id}`}
      onClick={() => navigate(`/workers/${worker.id}/detail`)}
      className="group bg-[#1E2836] rounded-2xl py-5 px-6 cursor-pointer transition-all duration-200 hover:-translate-y-[3px] hover:bg-[#2A3648] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.1)] shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)] hover:shadow-[0_2px_6px_rgba(0,0,0,0.16),0_12px_28px_rgba(0,0,0,0.14)]"
    >
      <div className="flex items-start gap-3.5">
        <div className="w-10 h-10 rounded-[10px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0 overflow-hidden">
          <img
            src={getWorkerIcon(worker.outcome_category, worker.name)}
            alt=""
            className="w-10 h-10 rounded-[10px] object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[14px] font-semibold text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors truncate">
            {display.name}
          </h3>
          {display.subtitle && (
            <p className="text-[12px] text-[#5C6778] mt-0.5 line-clamp-1">{display.subtitle}</p>
          )}
          {/* PART 4 — Live signal: pulsing green dot for active workers */}
          <div className="flex items-center gap-1.5 mt-2">
            {isActive ? (
              <span className="relative flex h-[7px] w-[7px]">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] animate-breathe" />
                <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#4ADE80]" />
              </span>
            ) : (
              <span className="w-[7px] h-[7px] rounded-full bg-[rgba(255,255,255,0.25)]" />
            )}
            <span className={`text-[11px] font-medium ${isActive ? 'text-[#4ADE80]' : 'text-[rgba(255,255,255,0.4)]'}`}>
              {statusLabel}
            </span>
            {needsReview > 0 && (
              <span className="text-[10px] text-[#F59E0B] bg-[rgba(245,158,11,0.08)] px-1.5 py-0.5 rounded-md font-medium ml-1">
                {needsReview} waiting for approval
              </span>
            )}
          </div>
          {/* PART 3 — Last action secondary line */}
          {lastAction && (
            <p className="text-[11px] text-[#5C6778] mt-1.5 truncate" data-testid={`worker-last-action-${worker.id}`}>
              Last action: {lastAction}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── "+ New Worker" CTA Card ── */
const NewWorkerCard = ({ navigate }) => (
  <div
    data-testid="new-worker-cta"
    onClick={() => navigate('/workers/setup')}
    className="group rounded-2xl border border-dashed border-[rgba(255,255,255,0.1)] py-5 px-6 cursor-pointer transition-all duration-200 hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.03)] flex flex-col items-center justify-center"
  >
    <div className="w-11 h-11 rounded-xl bg-[rgba(255,255,255,0.06)] flex items-center justify-center mb-3 group-hover:bg-[rgba(0,229,192,0.08)] transition-colors">
      <Plus size={20} className="text-[#7D8896] group-hover:text-[#00E5C0] transition-colors" />
    </div>
    <span className="text-sm font-medium text-[#7D8896] group-hover:text-[#00E5C0] transition-colors">+ Create an orchestration</span>
  </div>
);

/* ── Activation Proof: "Your AI is live" demo ── */
const DEMO_PHASES = ['idle', 'loading', 'enquiry', 'processing', 'response', 'done'];

const ActivationProof = ({ orgId, navigate, onNewWorker }) => {
  const [phase, setPhase] = useState('idle');
  const [demo, setDemo] = useState(null);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('activation_demo_seen') === '1');
  const [showFull, setShowFull] = useState(false);

  const runDemo = useCallback(async () => {
    if (!orgId) return;
    setPhase('loading');
    setError(false);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/activation/live-demo`, {
        method: 'POST',
      }, orgId);
      if (!res.ok) throw new Error('Demo failed');
      const data = await res.json();
      setDemo(data);

      // Animate through phases with delays
      setPhase('enquiry');
      setTimeout(() => setPhase('processing'), 2200);
      setTimeout(() => setPhase('response'), 4000);
      setTimeout(() => setPhase('done'), 5500);
    } catch {
      setError(true);
      setPhase('idle');
    }
  }, [orgId]);

  // Auto-trigger on first mount if not previously dismissed
  useEffect(() => {
    if (!dismissed && phase === 'idle') {
      runDemo();
    }
  }, [dismissed, phase, runDemo]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('activation_demo_seen', '1');
  };

  // Dismissed state: compact confirmation with next actions
  if (dismissed) {
    return (
      <div data-testid="system-ready-actions">
        <div className="flex items-center gap-2.5 bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.1)] rounded-xl px-4 py-3 mb-4">
          <ShieldCheck size={16} className="text-[#00E5C0]" weight="fill" />
          <p className="text-xs text-[#B0B8C4]">Your AI is actively handling enquiries for your business.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/inbox')} data-testid="action-view-activity"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-medium bg-[rgba(255,255,255,0.04)] text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-all">
            <Eye size={12} /> View activity
          </button>
          <button onClick={() => onNewWorker ? onNewWorker() : navigate('/workers/new')} data-testid="action-add-worker"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-medium bg-[rgba(255,255,255,0.04)] text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-all">
            <Plus size={12} /> Add workers
          </button>
          <button onClick={() => navigate('/settings')} data-testid="action-settings"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-medium bg-[rgba(255,255,255,0.04)] text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-all">
            <GearSix size={12} /> Settings
          </button>
        </div>
      </div>
    );
  }

  // Idle / error / loading before demo data
  if (phase === 'idle' || phase === 'loading') {
    return (
      <div data-testid="activation-loading" className="text-center py-4">
        {error ? (
          <div className="space-y-3">
            <p className="text-xs text-[#7D8896]">Could not load demo. Your system is still ready.</p>
            <button onClick={runDemo} className="text-[11px] text-[#00E5C0] hover:underline">Try again</button>
            <button onClick={handleDismiss} className="text-[11px] text-[#7D8896] hover:text-[#B0B8C4] ml-4">Skip</button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <CircleNotch size={14} className="text-[#00E5C0] animate-spin" />
            <p className="text-xs text-[#7D8896]">Preparing your live demo...</p>
          </div>
        )}
      </div>
    );
  }

  // Demo flow
  const phaseIndex = DEMO_PHASES.indexOf(phase);
  const showEnquiry = phaseIndex >= 2;
  const showProcessing = phaseIndex >= 3;
  const showResponse = phaseIndex >= 4;
  const isDone = phaseIndex >= 5;

  return (
    <div data-testid="activation-proof" className="space-y-4">
      {/* Hero confirmation */}
      <div className="text-center py-2">
        <p className="text-base font-semibold text-[#F0F4F8]" data-testid="activation-headline">
          Just handled for you
        </p>
      </div>

      {/* Phase 1: Incoming enquiry — compact */}
      <div
        className={`rounded-xl border transition-all duration-500 ${
          showEnquiry ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        } ${showProcessing ? 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.01)]' : 'border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.02)]'}`}
        style={{ transitionProperty: 'opacity, transform, border-color, background-color' }}
        data-testid="demo-enquiry"
      >
        {showEnquiry && (
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center">
                <UserCircle size={14} className="text-[#6366F1]" weight="fill" />
              </div>
              <span className="text-[11px] font-medium text-[#F0F4F8]">{demo?.enquiry?.customer_name}</span>
              <span className="text-[10px] text-[#5C6778] ml-auto">Just now</span>
            </div>
            <p className="text-[11px] font-medium text-[#B0B8C4] mb-0.5">{demo?.enquiry?.subject}</p>
            <p className="text-[11px] text-[#7D8896] leading-relaxed line-clamp-1">{demo?.enquiry?.body}</p>
          </div>
        )}
      </div>

      {/* Phase 2: Worker processing */}
      <div
        className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-500 ${
          showProcessing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        } ${showResponse ? 'bg-[rgba(255,255,255,0.01)]' : 'bg-[rgba(245,158,11,0.03)] border border-[rgba(245,158,11,0.1)]'}`}
        style={{ transitionProperty: 'opacity, transform, border-color, background-color' }}
        data-testid="demo-processing"
      >
        {showProcessing && (
          <>
            <div className="w-6 h-6 rounded-full bg-[rgba(245,158,11,0.1)] flex items-center justify-center shrink-0">
              {showResponse
                ? <CheckCircle size={14} className="text-[#00E5C0]" weight="fill" />
                : <CircleNotch size={14} className="text-[#F59E0B] animate-spin" />
              }
            </div>
            <div>
              <p className="text-[11px] font-medium text-[#F0F4F8]">{demo?.worker_name}</p>
              <p className="text-[10px] text-[#7D8896]">{demo?.processing?.action}</p>
            </div>
          </>
        )}
      </div>

      {/* Phase 3: Generated response — compact */}
      <div
        className={`rounded-xl border transition-all duration-500 ${
          showResponse ? 'opacity-100 translate-y-0 border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.02)]' : 'opacity-0 translate-y-2 border-transparent'
        }`}
        style={{ transitionProperty: 'opacity, transform, border-color, background-color' }}
        data-testid="demo-response"
      >
        {showResponse && (
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full bg-[rgba(0,229,192,0.1)] flex items-center justify-center">
                <Robot size={14} className="text-[#00E5C0]" weight="fill" />
              </div>
              <span className="text-[11px] font-medium text-[#00E5C0]">{demo?.response?.from}</span>
              <span className="text-[10px] text-[#5C6778] ml-auto">Draft ready</span>
            </div>
            <p className="text-[11px] font-medium text-[#B0B8C4] mb-0.5">{demo?.response?.subject}</p>
            <p className="text-[11px] text-[#7D8896] leading-relaxed line-clamp-1">{demo?.response?.body}</p>
          </div>
        )}
      </div>

      {/* Expandable full interaction */}
      {isDone && (demo?.enquiry?.body || demo?.response?.body) && (
        <div data-testid="full-interaction">
          <button
            onClick={() => setShowFull(!showFull)}
            className="text-[11px] text-[#7D8896] hover:text-[#B0B8C4] transition-colors flex items-center gap-1"
            data-testid="toggle-full-interaction"
          >
            {showFull ? 'Hide full interaction' : 'View full interaction'}
            <ArrowRight size={10} className={`transition-transform ${showFull ? 'rotate-90' : ''}`} />
          </button>
          {showFull && (
            <div className="mt-2 space-y-3 animate-in slide-in-from-top-1">
              {demo?.enquiry?.body && (
                <div className="rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] px-4 py-3">
                  <p className="text-[10px] text-[#5C6778] mb-1 font-medium uppercase tracking-wide">Original enquiry</p>
                  <p className="text-[11px] text-[#7D8896] leading-relaxed whitespace-pre-line">{demo.enquiry.body}</p>
                </div>
              )}
              {demo?.response?.body && (
                <div className="rounded-xl bg-[rgba(0,229,192,0.02)] border border-[rgba(0,229,192,0.08)] px-4 py-3">
                  <p className="text-[10px] text-[#00E5C0]/50 mb-1 font-medium uppercase tracking-wide">AI response</p>
                  <p className="text-[11px] text-[#7D8896] leading-relaxed whitespace-pre-line">{demo.response.body}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Done: next actions */}
      {isDone && (
        <div className="flex items-center justify-between pt-2" data-testid="demo-done-actions">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/inbox')} data-testid="action-view-activity"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-medium bg-[rgba(0,229,192,0.06)] text-[#00E5C0] border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-all">
              <Eye size={12} /> View what your AI is doing
            </button>
            <button onClick={() => navigate('/workers')} data-testid="action-view-workers"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[11px] font-medium bg-transparent text-[#7D8896] hover:text-[#B0B8C4] transition-all">
              <Robot size={12} /> Manage my workers
            </button>
          </div>
          <button onClick={handleDismiss} data-testid="demo-dismiss"
            className="text-[10px] text-[#5C6778] hover:text-[#7D8896] transition-colors">
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

/* ── System Readiness Card (Activation Checklist) ── */
const TOOL_CONFIG = {
  email:    { label: 'Email',    appName: 'Gmail',           Icon: EnvelopeSimple, color: '#EA4335' },
  calendar: { label: 'Calendar', appName: 'Google Calendar', Icon: CalendarBlank,  color: '#4285F4' },
};

const SystemReadinessCard = ({ audit, automations, onConnectTool, connecting, navigate, orgId }) => {
  if (!audit) return null;

  const workers = audit.audit || [];
  const profileComplete = audit.profile_complete === true;
  const hasActiveWorker = automations.some(a => a.status === 'active');

  const emailMissing = workers.some(w => (w.missing || []).includes('email'));
  const calendarMissing = workers.some(w => (w.missing || []).includes('calendar'));
  const emailConnected = !emailMissing;
  const calendarConnected = !calendarMissing;

  const steps = [
    {
      id: 'profile',
      label: 'Complete your business profile',
      sublabel: profileComplete ? 'Business name, services, and hours are set.' : 'Add your business details so workers know your context.',
      done: profileComplete,
      action: profileComplete ? null : { label: 'Complete profile', route: '/business-setup' },
    },
    {
      id: 'email',
      label: 'Connect your email',
      sublabel: emailConnected
        ? 'Workers can read and draft email replies.'
        : `${workers.filter(w => (w.missing || []).includes('email')).length} workers waiting for email.`,
      done: emailConnected,
      action: emailConnected ? null : { label: 'Connect', tool: 'email' },
    },
    {
      id: 'calendar',
      label: 'Connect your calendar',
      sublabel: calendarConnected
        ? 'Booking and reminders are linked.'
        : `${workers.filter(w => (w.missing || []).includes('calendar')).length} workers waiting for calendar.`,
      done: calendarConnected,
      action: calendarConnected ? null : { label: 'Connect', tool: 'calendar' },
    },
    {
      id: 'worker',
      label: 'Activate your first worker',
      sublabel: hasActiveWorker
        ? `${automations.filter(a => a.status === 'active').length} worker${automations.filter(a => a.status === 'active').length !== 1 ? 's' : ''} running.`
        : workers.length > 0 ? 'You have workers ready to activate.' : 'Create a worker to handle tasks.',
      done: hasActiveWorker,
      action: hasActiveWorker ? null : { label: workers.length > 0 ? 'View workers' : 'Create worker', route: '/workers' },
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const totalSteps = steps.length;
  const readinessPercent = Math.round((completedCount / totalSteps) * 100);
  const allDone = completedCount === totalSteps;

  // Dismissible state — persisted per user
  const storageKey = `sr_dismissed_${orgId || 'default'}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });

  const handleDismissCard = () => {
    setDismissed(true);
    try { localStorage.setItem(storageKey, '1'); } catch {}
  };

  const handleExpand = () => {
    setDismissed(false);
    try { localStorage.removeItem(storageKey); } catch {}
  };

  // Auto re-expand if a system issue is detected (readiness dropped below 100%)
  const hasIssue = !allDone;
  const showCompact = allDone && dismissed;

  // Compact bar: meaningful message
  if (showCompact) {
    return (
      <section data-testid="system-readiness-card">
        <div
          className="rounded-xl px-5 py-2.5 border border-[rgba(255,255,255,0.04)] flex items-center justify-between cursor-pointer hover:bg-[rgba(255,255,255,0.015)] transition-colors"
          onClick={handleExpand}
          data-testid="readiness-compact"
        >
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-[7px] w-[7px]">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#00E5C0] animate-breathe" />
              <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#00E5C0]" />
            </span>
            <span className="text-[12px] text-[#B0B8C4] font-medium">Your AI is actively handling enquiries</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate('/inbox'); }}
            className="text-[11px] text-[#5C6778] hover:text-[#00E5C0] font-medium flex items-center gap-1 transition-colors"
            data-testid="compact-view-activity"
          >
            View activity <ArrowRight size={10} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="system-readiness-card">
      <div className={`bg-[#212C3A] rounded-2xl p-7 border shadow-[0_1px_4px_rgba(0,0,0,0.16),0_8px_24px_rgba(0,0,0,0.12)] ${hasIssue ? 'border-[rgba(245,158,11,0.12)]' : 'border-[rgba(255,255,255,0.10)]'}`}
        style={{ borderTop: hasIssue ? undefined : '1px solid rgba(255,255,255,0.13)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${allDone ? 'bg-[rgba(0,229,192,0.1)]' : hasIssue ? 'bg-[rgba(245,158,11,0.08)]' : 'bg-[rgba(0,229,192,0.06)]'}`}>
              {allDone
                ? <ShieldCheck size={18} className="text-[#00E5C0]" weight="fill" />
                : hasIssue
                  ? <WarningCircle size={18} className="text-[#F59E0B]" weight="fill" />
                  : <Gauge size={18} className="text-[#00E5C0]" weight="duotone" />
              }
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#F0F4F8]">
                {allDone ? 'System ready' : hasIssue ? 'System needs attention' : 'Get your system running'}
              </h2>
              <p className="text-[11px] text-[#7D8896]">
                {allDone
                  ? 'Your workers are set up and ready to go.'
                  : `${completedCount} of ${totalSteps} steps complete`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-2xl font-bold tracking-tight ${
                readinessPercent === 100 ? 'text-[#00E5C0]' : readinessPercent >= 50 ? 'text-[#F59E0B]' : 'text-[#EF4444]'
              }`}
              data-testid="readiness-percent"
            >
              {readinessPercent}%
            </span>
            {allDone && (
              <button
                onClick={handleDismissCard}
                className="text-[10px] text-[#5C6778] hover:text-[#7D8896] transition-colors ml-1"
                data-testid="dismiss-readiness"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-[rgba(255,255,255,0.04)] rounded-full mb-5 overflow-hidden" data-testid="readiness-bar">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden"
            style={{
              width: `${readinessPercent}%`,
              background: readinessPercent === 100 ? '#00E5C0' : readinessPercent >= 50 ? '#F59E0B' : '#EF4444',
            }}
          >
            {readinessPercent === 100 && (
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
                  animation: 'shimmer 3s ease-in-out infinite',
                }}
              />
            )}
          </div>
        </div>

        {allDone ? (
          <ActivationProof orgId={orgId} navigate={navigate} onNewWorker={() => setShowNewWorkerModal(true)} />
        ) : (
          <div className="space-y-1" data-testid="readiness-checklist">
            {steps.map((step) => {
              const isConnecting = step.action?.tool && connecting === step.action.tool;
              return (
                <div
                  key={step.id}
                  className={`flex items-center justify-between rounded-xl px-4 py-3 transition-colors ${
                    step.done
                      ? 'bg-[rgba(0,229,192,0.02)]'
                      : 'bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]'
                  }`}
                  data-testid={`checklist-${step.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                      step.done
                        ? 'bg-[rgba(0,229,192,0.15)]'
                        : 'border border-[rgba(255,255,255,0.12)]'
                    }`}>
                      {step.done
                        ? <CheckCircle size={14} className="text-[#00E5C0]" weight="fill" />
                        : <span className="w-1.5 h-1.5 rounded-full bg-[rgba(255,255,255,0.15)]" />
                      }
                    </div>
                    <div className="min-w-0">
                      <p className={`text-xs font-medium ${step.done ? 'text-[#7D8896]' : 'text-[#F0F4F8]'}`}>
                        {step.label}
                      </p>
                      <p className="text-[10px] text-[#5C6778] truncate">{step.sublabel}</p>
                    </div>
                  </div>
                  {step.action && (
                    <button
                      onClick={() => {
                        if (step.action.tool) onConnectTool(step.action.tool);
                        else if (step.action.route) navigate(step.action.route);
                      }}
                      disabled={isConnecting}
                      data-testid={`checklist-action-${step.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)] transition-all disabled:opacity-50 shrink-0"
                    >
                      {isConnecting ? <CircleNotch size={11} className="animate-spin" /> : <ArrowRight size={11} />}
                      {isConnecting ? 'Connecting...' : step.action.label}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

/* ── Workers Section (PRIMARY — 3 col grid) ── */
const TeamWorkersSection = ({ automations, runs, navigate }) => (
  <section data-testid="team-workers-section">
    <div className="flex items-center justify-between mb-6 mt-2">
      <h2 className="text-sm font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] flex-1">Your workers</h2>
      {automations.length > 0 && (
        <button onClick={() => navigate('/workers')}
          className="text-xs text-[#5C6778] hover:text-[#00E5C0] font-medium flex items-center gap-1 transition-colors"
          data-testid="view-all-workers">
          View all <ArrowRight size={12} />
        </button>
      )}
    </div>
    {automations.length === 0 ? (
      <div className="rounded-2xl bg-[#1E2836] p-12 text-center">
        <Robot size={36} className="text-[#4E5563] mx-auto mb-3" weight="light" />
        <p className="text-base text-[#F0F4F8] font-medium mb-1">No workers yet</p>
        <p className="text-sm text-[#7D8896] mb-6 max-w-sm mx-auto">Create your first AI worker to start automating enquiries, follow-ups, and bookings.</p>
        <button onClick={() => navigate('/workers/setup')} data-testid="create-first-worker"
          className="px-6 py-3 bg-[#00E5C0] text-[#12161C] text-sm font-semibold rounded-xl hover:bg-[#00CCB0] transition-colors">
          + Create an orchestration
        </button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {automations.map(a => <TeamWorkerCard key={a.id} worker={a} runs={runs} navigate={navigate} />)}
        <NewWorkerCard navigate={navigate} />
      </div>
    )}
  </section>
);

/* ── Popular Worker Templates ── */
const PopularWorkers = ({ navigate }) => {
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/automations`);
        if (!res.ok) return;
        const data = await res.json();
        const all = data.templates || [];
        // Pick top 6 visible, prioritising trending
        const visible = all.filter(t => t.visibility === 'public_listed');
        visible.sort((a, b) => (b.install_count || 0) - (a.install_count || 0));
        setTemplates(visible.slice(0, 6));
      } catch { /* silent */ }
      setLoaded(true);
    };
    load();
  }, []);

  if (!loaded || templates.length === 0) return null;

  return (
    <section data-testid="popular-workers-section">
      <div className="flex items-center justify-between mb-4 mt-2">
        <h2 className="text-sm font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] flex-1">
          Popular Templates
        </h2>
        <button
          onClick={() => navigate('/workers/browse')}
          className="text-xs text-[#5C6778] hover:text-[#00E5C0] font-medium flex items-center gap-1 transition-colors"
          data-testid="browse-all-templates"
        >
          Browse all <ArrowRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map(t => (
          <div
            key={t.id}
            onClick={() => navigate(`/marketplace/${t.slug}`)}
            className="group bg-[#1E2836] border border-[rgba(255,255,255,0.06)] rounded-xl p-5 cursor-pointer hover:border-[rgba(0,229,192,0.25)] hover:bg-[#232F3E] transition-all"
            data-testid={`popular-template-${t.slug}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center flex-shrink-0">
                <Package size={18} className="text-[#00E5C0]" weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-white truncate group-hover:text-[#00E5C0] transition-colors">{t.name}</h3>
                <p className="text-xs text-[#7D8896] mt-1 line-clamp-2">{t.tagline || t.description || ''}</p>
                <div className="flex items-center gap-3 mt-3">
                  {t.install_count > 0 && (
                    <span className="text-[10px] text-[#5C6778]">{t.install_count} installs</span>
                  )}
                  <span className="text-[10px] text-[#00E5C0] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Install <ArrowRight size={10} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};


/* ── Needs Your Attention ── */
const AttentionSection = ({ workItems, audit, navigate }) => {
  // Context-specific title based on item data
  const getAttentionTitle = (item) => {
    const trigger = (item.trigger_source || '').toLowerCase();
    const isBooking = trigger.includes('book') || trigger.includes('schedule') || (item.automation_name || '').toLowerCase().includes('book');
    const isQuote = trigger.includes('quote') || trigger.includes('invoice');

    if (isBooking) return 'Confirm booking time';
    if (isQuote) return 'Quote needs approval';
    if (item.reply) return 'Customer waiting for reply';
    if (trigger === 'email') return 'Customer waiting for reply';
    return 'Needs your review';
  };

  // Build system issues from audit data
  const systemIssues = [];
  if (audit) {
    const workers = audit.audit || [];
    const emailMissing = workers.some(w => (w.missing || []).includes('email'));
    const calendarMissing = workers.some(w => (w.missing || []).includes('calendar'));
    const pausedWorkers = workers.filter(w => w.classification === 'paused' || w.status === 'paused');

    if (emailMissing) {
      systemIssues.push({
        id: 'email-missing',
        headline: 'Email not connected',
        detail: 'Cannot respond to enquiries — connect your email to start',
        icon: EnvelopeSimple,
        color: '#EF4444',
        route: '/apps',
      });
    }
    if (calendarMissing) {
      systemIssues.push({
        id: 'calendar-missing',
        headline: 'Calendar not connected',
        detail: 'Booking unavailable — connect your calendar',
        icon: CalendarBlank,
        color: '#F59E0B',
        route: '/apps',
      });
    }
    pausedWorkers.forEach(w => {
      const name = w.name || w.automation_name || 'Worker';
      const nameLower = name.toLowerCase();
      if (nameLower.includes('follow')) {
        systemIssues.push({
          id: `paused-${w.automation_id}`,
          headline: 'Follow-up paused',
          detail: 'Leads not being chased — reactivate to resume',
          icon: ArrowClockwise,
          color: '#F59E0B',
          route: `/workers/${w.automation_id}/detail`,
        });
      }
    });
  }

  const totalItems = workItems.length + systemIssues.length;

  return (
    <section data-testid="attention-section">
      <div className="flex items-center justify-between mb-5 mt-2">
        <h2 className="text-sm font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] flex-1">
          Needs your attention
        </h2>
        <div className="flex items-center gap-3">
          {totalItems > 0 && (
            <span className="text-[11px] font-normal text-[#F59E0B] bg-[rgba(245,158,11,0.06)] px-2 py-0.5 rounded-full">{totalItems}</span>
          )}
          <button onClick={() => navigate('/inbox')} className="text-xs text-[#7D8896] hover:text-[#00E5C0] font-medium flex items-center gap-1 transition-colors">
            View all <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {totalItems === 0 ? (
        <div className="py-8 text-center">
          <CheckCircle size={28} className="text-[#00E5C0] mx-auto mb-2" weight="light" />
          <p className="text-sm text-[#F0F4F8] font-medium">All clear</p>
          <p className="text-xs text-[#7D8896] mt-1">When your workers need a decision, it will show up here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* System issues first */}
          {systemIssues.map((issue) => {
            const Icon = issue.icon;
            return (
              <div key={issue.id} data-testid={`attention-${issue.id}`}
                onClick={() => navigate(issue.route)}
                className="group flex items-center gap-3.5 py-3 px-4 rounded-xl cursor-pointer transition-all duration-200 border border-[rgba(239,68,68,0.08)] hover:border-[rgba(239,68,68,0.15)] bg-[rgba(239,68,68,0.02)] hover:bg-[rgba(239,68,68,0.04)]">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${issue.color}10` }}>
                  <Icon size={15} style={{ color: issue.color }} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#F0F4F8] font-medium">{issue.headline}</p>
                  <p className="text-[11px] text-[#7D8896] mt-0.5">{issue.detail}</p>
                </div>
                <span className="text-[10px] text-[#EF4444] bg-[rgba(239,68,68,0.06)] px-2 py-1 rounded-md font-medium shrink-0">
                  Fix now
                </span>
              </div>
            );
          })}

          {/* Work items */}
          {workItems.slice(0, 5).map((item) => (
            <div
              key={item.id}
              data-testid={`attention-item-${item.id}`}
              onClick={() => navigate('/inbox')}
              className="group flex items-center gap-3.5 py-3 px-4 rounded-xl cursor-pointer transition-all duration-200 border border-[rgba(245,158,11,0.06)] hover:border-[rgba(245,158,11,0.12)] hover:bg-[rgba(255,255,255,0.015)]"
            >
              <div className="w-8 h-8 rounded-lg bg-[rgba(245,158,11,0.06)] flex items-center justify-center shrink-0">
                <Warning size={15} className="text-[#F59E0B]" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-[#F0F4F8] font-medium">{getAttentionTitle(item)}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {item.customer_name && (
                    <span className="text-[11px] text-[#B0B8C4] font-medium">{item.customer_name}</span>
                  )}
                  {item.customer_name && item.automation_name && <span className="text-[10px] text-[#2A2F38]">·</span>}
                  <span className="text-[11px] text-[#7D8896] truncate">
                    {item.summary || item.automation_name || 'Review this item to continue'}
                  </span>
                </div>
              </div>
              <span className="text-[10px] text-[#F59E0B] bg-[rgba(245,158,11,0.06)] px-2 py-1 rounded-md font-medium shrink-0">
                Waiting for you
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/* ═══════════════════════════════════════════════ */
/*  LEGACY DASHBOARD (fallback)                    */
/* ═══════════════════════════════════════════════ */

const SUGGESTIONS = [
  { label: 'Handle new enquiries', intent: 'Handle new customer enquiries and draft replies' },
  { label: 'Follow up missed leads', intent: 'Follow up with missed leads who didn\'t respond' },
  { label: 'Set up booking assistant', intent: 'Help customers book appointments and check availability' },
  { label: 'Send review requests', intent: 'Send review requests to customers after completing a job' },
  { label: 'Connect my email', route: '/apps' },
];

const LegacyCommandHero = ({ navigate }) => {
  const openCommand = (inputText) => {
    window.dispatchEvent(new CustomEvent('open-command-drawer', { detail: { input: inputText || '' } }));
  };
  return (
    <section data-testid="command-hero">
      <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 md:p-8">
        <h2 className="text-lg font-semibold text-[#F0F4F8] mb-1">What do you want your AI to help with?</h2>
        <p className="text-sm text-[#7D8896] mb-5">Describe what you need — we'll create an AI worker for you.</p>
        <button onClick={() => openCommand()} data-testid="open-command-btn"
          className="w-full flex items-center gap-3 px-4 py-3.5 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#5C6778] hover:border-[rgba(0,229,192,0.2)] hover:text-[#7D8896] transition-colors text-left">
          <Sparkle size={16} className="text-[#7D8896]" />
          <span className="flex-1">e.g. Reply to customer enquiries automatically...</span>
          <span className="text-[10px] text-[#5C6778] font-mono bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 rounded">&#8984;K</span>
        </button>
        <div className="flex flex-wrap gap-2 mt-4">
          {SUGGESTIONS.map((s) => (
            <button key={s.label} onClick={() => { if (s.route) { navigate(s.route); return; } openCommand(s.intent); }}
              data-testid={`suggestion-${s.label.toLowerCase().replace(/\s/g, '-')}`}
              className="px-3.5 py-2 bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.12)] rounded-lg text-xs text-[#B0B8C4] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.25)] transition-all">
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const LegacyTodayCards = ({ workCount, activeWorkers, activityToday, navigate }) => {
  const cards = [
    { label: 'Work needing attention', value: workCount, color: workCount > 0 ? '#F59E0B' : '#7D8896', icon: Bell, onClick: () => navigate('/inbox') },
    { label: 'Workers active', value: activeWorkers, color: activeWorkers > 0 ? '#00E5C0' : '#7D8896', icon: Robot, onClick: () => navigate('/workers') },
    { label: 'Activity today', value: activityToday, color: activityToday > 0 ? '#3B82F6' : '#7D8896', icon: Lightning, onClick: null },
  ];
  return (
    <section data-testid="today-section">
      <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em] mb-3">Today in your business</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`} onClick={c.onClick}
              className={`bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 ${c.onClick ? 'cursor-pointer hover:border-[rgba(255,255,255,0.12)]' : ''} transition-colors`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} style={{ color: c.color }} weight="duotone" />
                <span className="text-xs text-[#7D8896]">{c.label}</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight" style={{ color: c.color }}>{c.value}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const LegacyWorkSection = ({ workItems, navigate }) => {
  const actionLabel = (item) => {
    if (item.reply) return 'Review draft reply';
    if (item.trigger_source === 'email') return 'Review inbound enquiry';
    return 'Review and respond';
  };
  return (
    <section data-testid="work-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em]">Work needing your attention</h2>
        {workItems.length > 0 && (
          <button onClick={() => navigate('/inbox')} className="text-xs text-[#00E5C0] hover:text-[#00CCB0] font-medium flex items-center gap-1 transition-colors">View all <ArrowRight size={12} /></button>
        )}
      </div>
      {workItems.length === 0 ? (
        <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-8 text-center">
          <CheckCircle size={28} className="text-[#00E5C0] mx-auto mb-2" weight="light" />
          <p className="text-sm text-[#F0F4F8] font-medium">Nothing needs your attention right now</p>
          <p className="text-xs text-[#7D8896] mt-1 mb-4">When your workers need a decision, it will show up here.</p>
          <button onClick={() => navigate('/workers/setup')} data-testid="setup-first-worker"
            className="px-4 py-2 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] text-xs font-medium rounded-lg hover:bg-[rgba(0,229,192,0.14)] border border-[rgba(0,229,192,0.15)] transition-colors">
            Set up your first AI worker
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {workItems.slice(0, 5).map((item) => (
            <div key={item.id} data-testid={`work-item-${item.id}`} onClick={() => navigate('/inbox')}
              className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:border-[rgba(245,158,11,0.2)] transition-colors">
              <div className="w-8 h-8 rounded-lg bg-[rgba(245,158,11,0.08)] flex items-center justify-center flex-shrink-0">
                <Warning size={16} className="text-[#F59E0B]" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#F0F4F8] font-medium">{actionLabel(item)}</p>
                <p className="text-xs text-[#7D8896] mt-0.5 truncate">{item.customer_name}{item.automation_name ? ` · via ${item.automation_name}` : ''}</p>
              </div>
              <span className="text-[10px] text-[#F59E0B] bg-[rgba(245,158,11,0.08)] px-2 py-1 rounded-md font-medium flex-shrink-0">Needs review</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const LegacyWorkersSection = ({ automations, navigate }) => (
  <section data-testid="workers-section">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em]">Your workers</h2>
      <button onClick={() => navigate('/workers')} className="text-xs text-[#00E5C0] hover:text-[#00CCB0] font-medium flex items-center gap-1 transition-colors" data-testid="view-all-workers">View all <ArrowRight size={12} /></button>
    </div>
    {automations.length === 0 ? (
      <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-8 text-center">
        <Robot size={28} className="text-[#7D8896] mx-auto mb-2" weight="light" />
        <p className="text-sm text-[#F0F4F8] font-medium">No workers yet</p>
        <p className="text-xs text-[#7D8896] mt-1 mb-4">Create a worker to handle enquiries, follow up leads, or manage bookings.</p>
        <button onClick={() => navigate('/workers/setup')} data-testid="create-first-worker"
          className="px-4 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors">Set up your first worker</button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {automations.map((a) => (
          <div key={a.id} data-testid={`worker-card-${a.id}`}
            className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 hover:border-[rgba(255,255,255,0.1)] transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.status === 'active' ? 'bg-[rgba(0,229,192,0.08)]' : 'bg-[rgba(107,118,133,0.08)]'}`}>
                  <Robot size={16} className={a.status === 'active' ? 'text-[#00E5C0]' : 'text-[#7D8896]'} weight="duotone" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#F0F4F8]">{a.name}</h3>
                  <span className={`text-[10px] font-medium ${a.status === 'active' ? 'text-[#00E5C0]' : 'text-[#7D8896]'}`}>{(() => {
                    const t = (a.automation_type || '').toLowerCase();
                    const isProc = t.includes('event') || t.includes('enquiry') || t.includes('trigger') || t.includes('lead') || t.includes('booking');
                    return a.status === 'active' ? (isProc ? 'Handling enquiries' : 'Ready to use') : (isProc ? 'Needs setup to start working' : 'Paused');
                  })()}</span>
                </div>
              </div>
              {a.requires_review && (
                <span className="text-[9px] text-[#F59E0B] bg-[rgba(245,158,11,0.08)] px-1.5 py-0.5 rounded font-medium">Waiting for your approval</span>
              )}
            </div>
            {a.steps && a.steps.length > 0 && (
              <p className="text-xs text-[#7D8896] mb-3 line-clamp-1">{a.steps.map((s) => s.label).join(' → ')}</p>
            )}
            <div className="flex items-center justify-between text-xs text-[#5C6778]">
              <span>{a.run_count || 0} tasks completed{a.last_run ? ` · Last: ${new Date(a.last_run).toLocaleDateString()}` : ''}</span>
              <button onClick={() => navigate(`/workers/${a.id}`)} className="text-[#B0B8C4] hover:text-[#F0F4F8] transition-colors" data-testid={`view-worker-${a.id}`}><Eye size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    )}
  </section>
);

const TOOLS = [
  { id: 'email', name: 'Email', icon: EnvelopeSimple, color: '#EA4335' },
  { id: 'calendar', name: 'Calendar', icon: CalendarBlank, color: '#4285F4' },
  { id: 'phone', name: 'Phone', icon: Phone, color: '#00E5C0' },
  { id: 'sms', name: 'SMS', icon: ChatText, color: '#10B981' },
];

const LegacyToolsSection = ({ navigate }) => (
  <section data-testid="tools-section">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em]">Connected tools</h2>
      <button onClick={() => navigate('/apps')} className="text-xs text-[#00E5C0] hover:text-[#00CCB0] font-medium flex items-center gap-1 transition-colors">Manage <ArrowRight size={12} /></button>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {TOOLS.map((tool) => {
        const Icon = tool.icon;
        return (
          <div key={tool.id} data-testid={`tool-${tool.id}`} onClick={() => navigate('/apps')}
            className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 flex flex-col items-center gap-2.5 cursor-pointer hover:border-[rgba(255,255,255,0.12)] transition-colors">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tool.color}10` }}><Icon size={20} style={{ color: tool.color }} weight="duotone" /></div>
            <span className="text-xs text-[#B0B8C4] font-medium">{tool.name}</span>
            <span className="text-[10px] text-[#5C6778]">Not connected</span>
          </div>
        );
      })}
    </div>
  </section>
);

const LegacyActivitySection = ({ runs, navigate }) => {
  const activityLabel = (run) => {
    const src = run.trigger_source;
    if (src === 'email') return 'Processed inbound email';
    if (src === 'manual') return 'Handled customer enquiry';
    if (src === 'manual_test') return 'Worker test completed';
    return run.summary || 'Activity completed';
  };
  return (
    <section data-testid="activity-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-[0.08em]">Recent activity</h2>
        {runs.length > 0 && (
          <button onClick={() => navigate('/inbox')} className="text-xs text-[#00E5C0] hover:text-[#00CCB0] font-medium flex items-center gap-1 transition-colors">View all <ArrowRight size={12} /></button>
        )}
      </div>
      {runs.length === 0 ? (
        <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 text-center">
          <Clock size={24} className="text-[#5C6778] mx-auto mb-2" weight="light" />
          <p className="text-xs text-[#7D8896]">No activity yet. It will show up here once your workers start handling tasks.</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {runs.slice(0, 6).map((run) => (
            <div key={run.id} data-testid={`activity-${run.id}`}
              className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-lg px-4 py-3 flex items-center gap-3 hover:border-[rgba(255,255,255,0.1)] transition-colors">
              <div className="w-6 h-6 rounded-md bg-[rgba(59,130,246,0.08)] flex items-center justify-center flex-shrink-0"><ArrowClockwise size={12} className="text-[#3B82F6]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#F0F4F8]">{activityLabel(run)}</p>
                <p className="text-[10px] text-[#5C6778] mt-0.5 truncate">{run.automation_name || 'Worker'}{run.input?.customer_name ? ` · ${run.input.customer_name}` : ''}</p>
              </div>
              <span className="text-[10px] text-[#5C6778] flex-shrink-0">{timeAgo(run.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/* ─────────────────────────────────────────────── */
/*  ONBOARDING                                     */
/* ─────────────────────────────────────────────── */
import OnboardingWizard from '../components/OnboardingWizard';
import { connectApp, openOAuthPopup } from '../utils/composioAuth';

/* ─────────────────────────────────────────────── */
/*  MAIN PAGE                                      */
/* ─────────────────────────────────────────────── */

export default function ServiceHome() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { user } = useAuth();
  const orgId = activeOrganization?.organization_id;
  const [automations, setAutomations] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [trustAudit, setTrustAudit] = useState(null);
  const [connectingTool, setConnectingTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [useNewDashboard] = useState(isDashboardV1);
  const [showNewWorkerModal, setShowNewWorkerModal] = useState(false);

  // Voice & Operator wizard state (bypass AutomationBuilder)
  const [showVoiceWizard, setShowVoiceWizard] = useState(false);
  const [voiceWizardAgentId, setVoiceWizardAgentId] = useState(null);
  const [showOperatorModal, setShowOperatorModal] = useState(false);

  const handleVoiceSelect = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: 'New Voice Agent',
            short_description: 'Voice agent for phone-based interactions',
            capability_profile: 'live_voice_s2s_agent',
            accepted_inputs: ['audio', 'text'],
            status: 'draft',
            is_active: false,
            org_id: orgId
          })
        },
        orgId
      );
      if (response.ok) {
        const data = await response.json();
        const id = data.agent_id || data.agent?.id || data.id;
        setVoiceWizardAgentId(id);
        setShowVoiceWizard(true);
      } else {
        toast.error('Failed to create voice worker');
      }
    } catch {
      toast.error('Failed to create voice worker');
    }
  };

  const handleOperatorSelect = () => {
    setShowOperatorModal(true);
  };

  const fetchAll = useCallback(async () => {
    if (!orgId) return; // Keep loading=true until orgId resolves
    try {
      const [autoRes, runsRes, workRes, trustRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/service-os/automations`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/service-os/runs`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/service-os/work`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/trust-audit`, {}, orgId),
      ]);
      if (autoRes.ok) { const d = await autoRes.json(); setAutomations(d.automations || []); }
      if (runsRes.ok) { const d = await runsRes.json(); setRuns(d.runs || []); }
      if (workRes.ok) { const d = await workRes.json(); setWorkItems(d.items || []); }
      if (trustRes.ok) { const d = await trustRes.json(); setTrustAudit(d); }
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handle tool connection from readiness card
  const handleConnectTool = useCallback(async (toolKey) => {
    const cfg = TOOL_CONFIG[toolKey];
    if (!cfg || !orgId) return;
    setConnectingTool(toolKey);
    try {
      const result = await connectApp(cfg.appName, orgId);
      if (result.redirect_url) {
        openOAuthPopup(result.redirect_url);
        // Poll for connection completion
        const pollInterval = setInterval(async () => {
          try {
            const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/trust-audit`, {}, orgId);
            if (res.ok) {
              const data = await res.json();
              setTrustAudit(data);
              // Check if tool is now connected
              const stillMissing = (data.audit || []).some(
                w => (w.missing || []).includes(toolKey)
              );
              if (!stillMissing) {
                clearInterval(pollInterval);
                setConnectingTool(null);
                const readyNow = (data.audit || []).filter(w => w.classification === 'ready').length;
                toast.success(`${cfg.label} connected. ${readyNow} worker${readyNow !== 1 ? 's' : ''} ${readyNow !== 1 ? 'are' : 'is'} now ready.`);
              }
            }
          } catch {}
        }, 3000);
        // Stop polling after 2 minutes
        setTimeout(() => { clearInterval(pollInterval); setConnectingTool(null); }, 120000);
      }
    } catch (err) {
      toast.error(`Could not connect ${cfg.label}. Try again from the Apps page.`);
      setConnectingTool(null);
    }
  }, [orgId]);

  useEffect(() => {
    if (!loading && !localStorage.getItem('mm_onboarding_done')) {
      setShowOnboarding(true);
    }
  }, [loading]);

  const greeting = user?.name ? user.name.split(' ')[0] : 'there';

  /* ── Legacy data ── */
  const activeWorkers = automations.filter((a) => a.status === 'active').length;
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const activityToday = runs.filter((r) => new Date(r.created_at) >= todayStart).length;

  return (
    <>
      <SEO title="Home" />

      {showOnboarding && (
        <OnboardingWizard onComplete={() => { setShowOnboarding(false); fetchAll(); }} />
      )}

      <div className="max-w-[1360px] mx-auto px-2" data-testid="service-home">
        {/* Greeting */}
        <div className="mb-12">
          <h1 className="text-2xl font-semibold text-[#F0F4F8]" data-testid="home-greeting">Hey {greeting}</h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={28} className="animate-spin text-[#00E5C0]" />
          </div>
        ) : useNewDashboard ? (
          /* ── NEW DASHBOARD V2 — AI Running Your Business ── */
          <div className="space-y-20">
            <div className="animate-section-enter" style={{ animationDelay: '0ms' }}>
              <TodaySummary runs={runs} workItems={workItems} />
            </div>
            <div className="animate-section-enter" style={{ animationDelay: '80ms' }}>
              <SystemReadinessCard audit={trustAudit} automations={automations} onConnectTool={handleConnectTool} connecting={connectingTool} navigate={navigate} orgId={orgId} />
            </div>
            <div className="animate-section-enter" style={{ animationDelay: '160ms' }}>
              <TeamWorkersSection automations={automations} runs={runs} navigate={navigate} />
            </div>
            <div className="animate-section-enter" style={{ animationDelay: '200ms' }}>
              <PopularWorkers navigate={navigate} />
            </div>
            <div className="animate-section-enter" style={{ animationDelay: '240ms' }}>
              <AttentionSection workItems={workItems} audit={trustAudit} navigate={navigate} />
            </div>
            <div className="animate-section-enter" style={{ animationDelay: '320ms' }}>
              <ActivityFeed runs={runs} navigate={navigate} />
            </div>
          </div>
        ) : (
          /* ── LEGACY DASHBOARD ── */
          <div className="space-y-8">
            <LegacyCommandHero navigate={navigate} />
            <LegacyTodayCards workCount={workItems.length} activeWorkers={activeWorkers} activityToday={activityToday} navigate={navigate} />
            <LegacyWorkSection workItems={workItems} navigate={navigate} />
            <LegacyWorkersSection automations={automations} navigate={navigate} />
            <LegacyToolsSection navigate={navigate} />
            <LegacyActivitySection runs={runs} navigate={navigate} />
          </div>
        )}
      </div>
      <NewWorkerModal
        isOpen={showNewWorkerModal}
        onClose={() => setShowNewWorkerModal(false)}
        onVoiceSelect={handleVoiceSelect}
        onOperatorSelect={handleOperatorSelect}
      />

      {/* Voice Wizard — renders on Dashboard, bypasses AutomationBuilder */}
      {showVoiceWizard && voiceWizardAgentId && (
        <div data-testid="voice-wizard-overlay-home">
          <UniversalVoiceWizard
            agentIdProp={voiceWizardAgentId}
            isModal={true}
            onClose={() => {
              setShowVoiceWizard(false);
              setVoiceWizardAgentId(null);
              navigate('/workers');
            }}
          />
        </div>
      )}

      {/* Operator Modal — renders on Dashboard, bypasses AutomationBuilder */}
      <CreateOperatorModal
        isOpen={showOperatorModal}
        onClose={() => setShowOperatorModal(false)}
        onSuccess={() => {
          setShowOperatorModal(false);
          navigate('/workers');
        }}
        orgId={orgId}
      />
    </>
  );
}
